import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../api';

import { useAuth } from '../contexts/AuthContext';

// Self-serve signup. Creates the account AND starts the free trial in one
// request, so the user lands straight in the chat — no admin step in between.
const SignUp = () => {
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Set once the account exists and the confirmation mail has gone out
  const [sentTo, setSentTo] = useState('');
  const [resending, setResending] = useState(false);
  const [resendNote, setResendNote] = useState('');
  const { signUp, error, clearError } = useAuth();
  const navigate = useNavigate();

  // Hiding the link on the sign-in page isn't enough — people bookmark /signup.
  // null = still checking, so the form isn't rendered and then yanked away.
  const [signupEnabled, setSignupEnabled] = useState(null);

  useEffect(() => {
    let cancelled = false;
    authAPI.config()
      .then(c => { if (!cancelled) setSignupEnabled(!!c.signupEnabled); })
      .catch(() => { if (!cancelled) setSignupEnabled(false); });
    return () => { cancelled = true; };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (error) clearError();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length > 50) {
      newErrors.name = 'Name cannot be more than 50 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    // Mirrors the server's rule so the user hears about it before a round trip
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || submitting) return;

    setSubmitting(true);
    const result = await signUp({
      name: formData.name.trim(),
      email: formData.email.trim(),
      password: formData.password
    });
    setSubmitting(false);

    if (result.verificationRequired) {
      setSentTo(result.email || formData.email.trim());
    } else if (result.success) {
      navigate('/chat');
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendNote('');
    try {
      await authAPI.resendVerification(sentTo);
      setResendNote('Sent. Give it a minute, and check your spam folder.');
    } catch {
      setResendNote('Could not resend right now. Please try again shortly.');
    } finally {
      setResending(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 bg-gray-700/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all ${
      errors[field] ? 'border-red-500' : 'border-gray-600'
    }`;

  if (signupEnabled === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
        <p className="text-gray-400">Loading…</p>
      </div>
    );
  }

  if (signupEnabled === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black px-4">
        <div className="max-w-md w-full bg-gray-800/50 backdrop-blur-md rounded-2xl p-8 border border-gray-700/30 shadow-2xl text-center">
          <h1 className="text-2xl font-bold text-white mb-3">Registration is closed</h1>
          <p className="text-gray-400 mb-6">
            New accounts aren't open right now. Please contact your administrator
            to have one created for you.
          </p>
          <Link
            to="/signin"
            className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2.5 px-6 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  // Account created, mail sent. The trial doesn't start until they click the
  // link, so there is nothing useful to show them in the app yet.
  if (sentTo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black px-4">
        <div className="max-w-md w-full bg-gray-800/50 backdrop-blur-md rounded-2xl p-8 border border-gray-700/30 shadow-2xl text-center">
          <div className="text-4xl mb-3">📨</div>
          <h1 className="text-2xl font-bold text-white mb-2">Check your inbox</h1>
          <p className="text-gray-400 mb-1">
            We've sent a confirmation link to
          </p>
          <p className="text-white font-medium mb-4 break-all">{sentTo}</p>
          <p className="text-gray-500 text-sm mb-6">
            Click it to activate your 7-day free trial. The link expires in 24 hours.
          </p>

          {resendNote && (
            <p className="text-teal-300 text-sm mb-4">{resendNote}</p>
          )}

          <button
            onClick={handleResend}
            disabled={resending}
            className="w-full bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-lg font-medium mb-3 disabled:opacity-60"
          >
            {resending ? 'Sending…' : "Didn't get it? Resend"}
          </button>
          <Link to="/signin" className="block text-blue-400 hover:text-blue-300 text-sm">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black px-4 py-8">
      <div className="max-w-md w-full bg-gray-800/50 backdrop-blur-md rounded-2xl p-8 border border-gray-700/30 shadow-2xl">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">ROUT TECHNICAL AI</h1>
          <p className="text-gray-400">Start your free trial</p>
        </div>

        <div className="mb-6 p-3 bg-teal-500/10 border border-teal-500/30 rounded-lg text-center">
          <p className="text-teal-300 text-sm font-medium">7 days free · 100 messages</p>
          <p className="text-gray-400 text-xs mt-1">No card required</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={inputClass('name')}
              placeholder="Enter your name"
            />
            {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={inputClass('email')}
              placeholder="Enter your email"
            />
            {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={inputClass('password')}
              placeholder="At least 8 characters"
            />
            {errors.password && <p className="mt-1 text-sm text-red-400">{errors.password}</p>}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Creating account…' : 'Create Free Account'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-400">
            Already have an account?{' '}
            <Link to="/signin" className="text-blue-400 hover:text-blue-300 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
